import { useCallback, useEffect, useRef, useState } from 'react';
import { ClubCandidate, createClubPager, preloadClub, ReadyClub, shuffleClubs } from '../utils/featuredClubs';

export const FEATURED_CLUB_COUNT = 7;
const READY_BUFFER_SIZE = 8;
const REFILL_THRESHOLD = 4;
const DOWNLOAD_LIMIT = 4;

export interface ClubTile {
  current: ReadyClub | null;
  recentIds: string[];
}

interface ClubWallState {
  tiles: ClubTile[];
  status: 'loading' | 'ready' | 'empty' | 'error';
}

const emptyTiles = (count: number): ClubTile[] => Array.from({ length: count }, () => ({
  current: null, recentIds: [],
}));

/** Section-local controller: no subscriptions, authentication, writes or persistent browser storage. */
class ClubWall {
  private state: ClubWallState;
  private readPage = createClubPager();
  private metadata: ClubCandidate[] = [];
  private ready: ReadyClub[] = [];
  private downloads = new Map<string, AbortController>();
  private failed = new Set<string>();
  private consecutiveFailures = 0;
  private cooldownUntil = 0;
  private active = false;
  private disposed = false;
  private reading = false;
  private nextReadAt = 0;
  private passYield = 0;
  private pumpTimer: number | undefined;
  private filling = true;

  constructor(private publish: (state: ClubWallState) => void, count: number) {
    this.state = { tiles: emptyTiles(count), status: 'loading' };
  }

  private emit() {
    if (!this.disposed) this.publish({ ...this.state, tiles: [...this.state.tiles] });
  }

  private activeIds() {
    return new Set(this.state.tiles.map((tile) => tile.current?.id)
      .filter((id): id is string => Boolean(id)));
  }

  private occupiedIds() {
    return new Set([
      ...Array.from(this.activeIds()),
      ...this.ready.map((club) => club.id),
      ...Array.from(this.downloads.keys()),
    ].filter((id): id is string => Boolean(id)));
  }

  private schedulePump(delay = 100) {
    if (!this.active || this.disposed || this.pumpTimer !== undefined) return;
    this.pumpTimer = window.setTimeout(() => {
      this.pumpTimer = undefined;
      void this.pump();
    }, delay);
  }

  private accept(club: ReadyClub) {
    if (this.disposed || this.activeIds().has(club.id) || this.ready.some((item) => item.id === club.id)) return;
    this.consecutiveFailures = 0;
    const vacant = this.state.tiles.findIndex((tile) => !tile.current);
    if (vacant >= 0) {
      this.state.tiles[vacant] = { ...this.state.tiles[vacant], current: club };
      this.state.status = this.state.tiles.every((tile) => tile.current) ? 'ready' : 'loading';
      this.emit();
    } else if (this.ready.length < READY_BUFFER_SIZE) {
      this.ready.push(club);
    }
  }

  private async pump() {
    if (!this.active || this.disposed) return;
    if (Date.now() < this.cooldownUntil) {
      this.schedulePump(this.cooldownUntil - Date.now());
      return;
    }
    const vacancies = this.state.tiles.filter((tile) => !tile.current).length;
    if (this.ready.length < REFILL_THRESHOLD || vacancies) this.filling = true;
    if (!this.filling) return;
    if (this.ready.length >= READY_BUFFER_SIZE && !vacancies) {
      this.filling = false;
      return;
    }
    while (this.downloads.size < DOWNLOAD_LIMIT && this.metadata.length &&
      this.ready.length + this.downloads.size < READY_BUFFER_SIZE + vacancies) {
      const club = this.metadata.shift()!;
      if (this.occupiedIds().has(club.id) || this.failed.has(club.id)) continue;
      const controller = new AbortController();
      this.downloads.set(club.id, controller);
      void preloadClub(club, controller.signal).then((loaded) => {
        if (!this.active || this.disposed) {
          if (!this.disposed) this.metadata.unshift(club);
          return;
        }
        this.accept(loaded);
      }).catch((error: Error) => {
        if (this.disposed) return;
        if (error.name === 'AbortError') this.metadata.unshift(club);
        else {
          this.failed.add(club.id);
          this.consecutiveFailures++;
          if (this.consecutiveFailures >= 12) {
            this.consecutiveFailures = 0;
            this.cooldownUntil = Date.now() + 60000;
            if (!this.state.tiles.some(tile => tile.current)) {
              this.state.status = 'error';
              this.emit();
            }
          }
        }
      }).finally(() => {
        this.downloads.delete(club.id);
        this.schedulePump();
      });
    }

    if (this.ready.length + this.downloads.size >= READY_BUFFER_SIZE + vacancies || this.metadata.length) return;
    if (this.reading) return;
    if (Date.now() < this.nextReadAt) {
      this.schedulePump(Math.max(100, this.nextReadAt - Date.now()));
      return;
    }
    this.reading = true;
    try {
      const page = await this.readPage();
      if (this.disposed) return;
      const occupied = this.occupiedIds();
      const candidates = page.clubs.filter((club) => !occupied.has(club.id) && !this.failed.has(club.id));
      this.metadata.push(...shuffleClubs(candidates));
      this.passYield += candidates.length;
      this.nextReadAt = Date.now() + 500;
      if (page.endOfPass) {
        if (this.passYield === 0) {
          // A small/exhausted catalogue must not produce a rapid scan of exhausted metadata.
          this.nextReadAt = Date.now() + 60000;
          if (!this.downloads.size) {
            this.state.status = this.state.tiles.some((tile) => tile.current) ? 'ready' : this.failed.size ? 'error' : 'empty';
            this.emit();
          }
        }
        this.passYield = 0;
      }
    } catch {
      if (!this.disposed) {
        this.nextReadAt = Date.now() + 30000;
        if (!this.state.tiles.some((tile) => tile.current)) this.state.status = 'error';
        this.emit();
      }
    } finally {
      this.reading = false;
      this.schedulePump();
    }
  }

  /** Called by the rail only after this slot has moved completely outside its viewport. */
  replaceOffscreen(index: number) {
    const tile = this.state.tiles[index];
    if (!this.active || this.disposed || !tile?.current) return;
    const occupied = this.activeIds();
    const eligible = this.ready.map((_, position) => position)
      .filter((position) => !occupied.has(this.ready[position].id) && !tile.recentIds.includes(this.ready[position].id));
    if (eligible.length) {
      const next = eligible[Math.floor(Math.random() * eligible.length)];
      const [current] = this.ready.splice(next, 1);
      this.state.tiles[index] = { current, recentIds: [tile.current.id, ...tile.recentIds].slice(0, 3) };
      this.emit();
    }
    // If no decoded replacement is eligible, keep the existing decoded club for this lap.
    this.schedulePump();
  }

  setActive(active: boolean) {
    if (this.active === active || this.disposed) return;
    this.active = active;
    if (active) this.schedulePump(0);
    else {
      window.clearTimeout(this.pumpTimer);
      this.pumpTimer = undefined;
      this.downloads.forEach((controller) => controller.abort());
    }
  }

  setCount(count: number) {
    if (this.disposed || this.state.tiles.length === count) return;
    const removed = this.state.tiles.slice(count);
    this.state.tiles = this.state.tiles.slice(0, count);
    removed.forEach((tile) => { if (tile.current) this.accept(tile.current); });
    while (this.state.tiles.length < count) {
      const [tile] = emptyTiles(1);
      // Retain decoded entries across viewport changes, without downloading a second image.
      if (this.ready.length) tile.current = this.ready.splice(Math.floor(Math.random() * this.ready.length), 1)[0];
      this.state.tiles.push(tile);
    }
    this.state.status = this.state.tiles.every((tile) => tile.current) ? 'ready' : 'loading';
    this.emit();
    this.schedulePump(0);
  }

  retry() {
    this.failed.clear();
    this.consecutiveFailures = 0;
    this.cooldownUntil = 0;
    this.readPage = createClubPager();
    this.metadata = [];
    this.passYield = 0;
    this.nextReadAt = 0;
    this.state.status = this.state.tiles.some((tile) => tile.current) ? 'ready' : 'loading';
    this.emit();
    window.clearTimeout(this.pumpTimer);
    this.pumpTimer = undefined;
    this.schedulePump(0);
  }

  dispose() {
    this.disposed = true;
    this.active = false;
    window.clearTimeout(this.pumpTimer);
    this.downloads.forEach((controller) => controller.abort());
    this.metadata = [];
    this.ready = [];
  }
}

export function useFeaturedClubs(active: boolean, count: number) {
  const [state, setState] = useState<ClubWallState>(() => ({ tiles: emptyTiles(count), status: 'loading' }));
  const initialCount = useRef(count);
  const wallRef = useRef<ClubWall | null>(null);
  useEffect(() => {
    const wall = new ClubWall(setState, initialCount.current);
    wallRef.current = wall;
    return () => { wall.dispose(); wallRef.current = null; };
  }, []);
  useEffect(() => { wallRef.current?.setCount(count); }, [count]);
  useEffect(() => { wallRef.current?.setActive(active); }, [active]);
  const replaceOffscreen = useCallback((index: number) => wallRef.current?.replaceOffscreen(index), []);
  return {
    ...state,
    replaceOffscreen,
    retry: () => wallRef.current?.retry(),
  };
}
